import keycloak from '@vindr/services/keycloak';
import useRefreshComponent from '@vindr/utils/hooks/useRefreshComponent';
import { Modal, message } from 'antd';
import { MessageInstance } from 'antd/es/message/interface';
import { ModalStaticFunctions } from 'antd/es/modal/confirm';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';

export interface GlobalContextProps {
  keycloak: typeof keycloak;
  message: MessageInstance;
  modal: Omit<ModalStaticFunctions, 'warn'>;
}

const GlobalStateContext = createContext<GlobalContextProps>({} as GlobalContextProps);
export const GlobalProvider = ({ children }: { children: React.ReactNode }) => {
  const [keycloakState] = useState(() => keycloak);
  const [_, keycloakListener] = useRefreshComponent();
  const [messageApi, messageContextHolder] = message.useMessage();
  const [modalApi, modalContextHolder] = Modal.useModal();

  const memorizedValues: GlobalContextProps = useMemo(
    () => ({
      keycloak: keycloakState,
      message: messageApi,
      modal: modalApi,
    }),
    [keycloakState, messageApi, modalApi]
  );

  useEffect(() => {
    const unsubscribe = keycloakState.subscribe(keycloakListener);

    return () => unsubscribe();
  }, [keycloakState, keycloakListener]);

  return (
    <GlobalStateContext.Provider value={memorizedValues}>
      {messageContextHolder}
      {modalContextHolder}
      {children}
    </GlobalStateContext.Provider>
  );
};

export default GlobalProvider;

export const useGlobalState = () => {
  const context = useContext(GlobalStateContext);

  if (!context) throw new Error('useGlobalState must be used within a GlobalStateContext');

  return context;
};
